<template>
  <b-row>
    <b-col sm="12">
      <b-card body-class="py-2">
        <section class="list-view-heading">
          <ModuleViewHeading
            :module-id="access.module_id"
            :profile-id="access.profile_id"
            :reference="access.view_reference"
            summary-only
          />
          <div class="list-view-heading__controls">
            <div class="list-view-heading__actions">
              <TabHeaderActions />
            </div>
            <div class="list-view-heading__controls-end">
              <b-button variant="link"
                        class="list-view-icon-btn"
                        :class="{ 'list-view-icon-btn--active': filtersOpen }"
                        v-b-tooltip.hover.bottom
                        :title="filtersOpen ? 'Ocultar filtros' : 'Mostrar filtros'"
                        :aria-label="filtersOpen ? 'Ocultar filtros' : 'Mostrar filtros'"
                        @click="filtersOpen = !filtersOpen">
                <AppIcon name="filter" :fill="filtersOpen" />
              </b-button>
              <b-button variant="link"
                        class="list-view-icon-btn list-view-icon-btn--primary"
                        v-b-tooltip.hover.bottom
                        title="Agregar Orden de Compra"
                        aria-label="Agregar Orden de Compra"
                        @click="add()">
                <AppIcon name="add" />
              </b-button>
            </div>
          </div>
        </section>

        <b-collapse v-model="filtersOpen">
          <div class="list-view-toolbar__filters">
            <div class="list-view-filter" style="width: 150px;">
              <label class="filter-label">Desde</label>
              <b-form-input type="date" size="sm" v-model="filters.date_start"></b-form-input>
            </div>
            <div class="list-view-filter" style="width: 150px;">
              <label class="filter-label">Hasta</label>
              <b-form-input type="date" size="sm" v-model="filters.date_end"></b-form-input>
            </div>
            <div class="list-view-filter" style="flex: 1 1 230px; max-width: 340px;">
              <label class="filter-label">Proveedores</label>
              <v-select :options="arr.filters.suppliers"
                        v-model="filters.suppliers"
                        placeholder="Todos"
                        :multiple="true"
                        :select-on-tab="true"
                        :get-option-key="getFilterOptionKey"></v-select>
            </div>
            <div class="list-view-filter" style="flex: 1 1 190px; max-width: 260px;">
              <label class="filter-label">Estados</label>
              <v-select :options="arr.filters.status"
                        v-model="filters.status"
                        placeholder="Todos"
                        :multiple="true"
                        :select-on-tab="true"
                        :get-option-key="getFilterOptionKey"></v-select>
            </div>
            <div class="list-view-filter" style="flex: 1 1 150px; max-width: 190px;">
              <label class="filter-label">ID de orden</label>
              <b-form-input
                type="search"
                placeholder="Buscar ID"
                v-model="filters.id"
                size="sm"
                @input="onInputIdFilter"
                @keyup.enter="filterPurchaseOrders(true)">
              </b-form-input>
            </div>
            <b-button variant="outline-dark" size="sm" @click="filterPurchaseOrders(true)" v-b-tooltip.hover title="Aplicar filtros" style="height: 31px; width: 31px; padding: 0; flex-shrink: 0;">
              <AppIcon name="filter" />
            </b-button>
          </div>
        </b-collapse>
      </b-card>

      <b-row>
        <b-col lg="12" id="view_list_purchase_orders">
          <b-card no-body>
            <b-row no-gutters>
              <b-col sm="12">
                <b-table class="app-table"
                        ref="table"
                        responsive
                        :stacked="isMobileViewport ? 'md' : false"
                        sticky-header="60vh"
                        :hover="true"
                        :small="true"
                        :fixed="true"
                        :items="table.items"
                        :fields="table.fields"
                        :current-page="table.currentPage"
                        :per-page="table.perPage"
                        @row-clicked="onRowClicked"
                        :tbody-tr-class="rowClass"
                        :busy="table.isBusy"
                        v-if="table.items.length || table.isBusy">

                  <template v-slot:table-colgroup="scope">
                    <col
                      v-for="field in scope.fields"
                      :key="field.key"
                      :style="{ width: field.width }">
                  </template>

                  <template v-slot:table-busy>
                    <div class="text-center text-danger my-2">
                      <b-spinner class="align-middle"></b-spinner>
                      <strong>Cargando...</strong>
                    </div>
                  </template>

                  <template v-slot:cell(voucher)="data">
                    <span>ORDEN DE COMPRA #{{data.item.id}}</span>
                  </template>

                  <template v-slot:cell(date)="data">
                    {{moment(data.item.date).format('DD/MM/YYYY')}}
                  </template>

                  <template v-slot:cell(suppliers_id)="data">
                    <div v-if="data.item.supplier">
                      <b-avatar :src="data.item.supplier.image"
                                v-if="data.item.supplier.image"
                                size="25px">
                      </b-avatar>
                      <b-avatar v-else
                                :text="avatarFromName(data.item.supplier.name).text"
                                :style="avatarFromName(data.item.supplier.name).style"
                                size="25px">
                      </b-avatar>
                      &nbsp;<b>{{data.item.supplier.name}}</b>
                    </div>
                  </template>

                  <template v-slot:cell(status)="data">
                    <b-badge :variant="getStatusVariant(data.item.status)">
                      {{data.item.status_label || getStatusLabel(data.item.status)}}
                    </b-badge>
                  </template>

                  <template v-slot:cell(quantity_pending)="data">
                    <span class="text-danger" v-if="parseFloat(data.item.quantity_pending)>0">
                      {{parseFloat(data.item.quantity_pending).toFixed(2)}}
                    </span>
                    <span v-else>
                      0.00
                    </span>
                  </template>

                  <template v-slot:cell(amount_total)="data">
                    {{Intl.NumberFormat('es-AR',{style:'currency',currency:'ARS'}).format(data.item.amount_total || 0)}}
                  </template>

                  <template v-slot:cell(f_action)="data">
                    <b-dropdown right
                                no-caret
                                size="sm"
                                variant="link"
                                boundary="window"
                                :toggle-class="isMobileViewport ? 'purchase-orders-action-toggle-labeled' : 'app-table-action-toggle'"
                                :class="{ 'pull-right': !isMobileViewport }">
                      <template #button-content>
                        <AppIcon name="more_vert" :label="isMobileViewport ? 'Opciones' : 'Acciones'" :class="{ 'mr-1': isMobileViewport }" />
                        <span v-if="isMobileViewport">Opciones</span>
                      </template>

                      <b-dropdown-item @click="sendPrinter(data.item)">
                        <AppIcon name="print" /> Imprimir
                      </b-dropdown-item>

                      <b-dropdown-header v-if="canShowStatusHeader(data.item)">Estados</b-dropdown-header>
                      <b-dropdown-item @click="updatePurchaseOrderStatus(data.item, 'cancelled')"
                                       v-if="canCancelPurchaseOrder(data.item)"
                                       class="text-danger">
                        <AppIcon name="cancel" class="text-danger" /> Cancelar
                      </b-dropdown-item>
                      <b-dropdown-item @click="updatePurchaseOrderStatus(data.item, 'pending')"
                                       v-if="canSetPendingPurchaseOrder(data.item)">
                        <AppIcon name="undo" class="text-warning" /> Pendiente
                      </b-dropdown-item>

                      <b-dropdown-header v-if="canShowActionsHeader(data.item)">Acciones</b-dropdown-header>
                      <b-dropdown-item @click="edit(data.item)" v-if="canEditPurchaseOrder(data.item)">
                        <AppIcon name="edit" /> Editar
                      </b-dropdown-item>
                      <b-dropdown-item @click="remove(data.item)" v-if="canDeletePurchaseOrder(data.item)">
                        <AppIcon name="delete" class="text-danger" /> Eliminar
                      </b-dropdown-item>
                    </b-dropdown>
                  </template>
                </b-table>
                <EmptyState v-else icon="cart" title="No se encontraron órdenes de compra" description="Ajustá los filtros o el rango de fechas para ampliar la búsqueda, o generá una orden nueva con el botón Agregar." />

                <div class="app-table-footer purchase-orders-table-footer">
                  <span class="list-view-toolbar__count" v-if="table.tablePaginate">{{ table.tablePaginate.total }} registros</span>
                </div>
              </b-col>
            </b-row>
          </b-card>
        </b-col>
      </b-row>

      <b-sidebar
          id="sidebar-purchase-order-detail"
          v-model="detailDrawer"
          right
          shadow
          backdrop
          title="Detalle de Orden de Compra"
          width="420px"
          @hidden="hideSub">
        <div class="list-view-drawer" v-if="itemSelected">
          <div class="list-view-drawer__header">
            <div class="list-view-drawer__title">
              <span class="list-view-drawer__code">ORDEN DE COMPRA #{{itemSelected.id}}</span>
              <div v-if="itemSelected.supplier">{{itemSelected.supplier.name}}</div>
              <div class="list-view-drawer__meta" v-if="itemSelected.date">
                {{moment(itemSelected.date).format('DD/MM/YYYY')}}
              </div>
            </div>
            <div class="list-view-drawer__nav">
              <b-button size="sm"
                        variant="outline-secondary"
                        :disabled="!canGoPrevPurchaseOrder"
                        v-b-tooltip.hover
                        title="Orden anterior"
                        @click="goPrevPurchaseOrder()">
                <AppIcon name="chevron-up" />
              </b-button>
              <b-button size="sm"
                        variant="outline-secondary"
                        :disabled="!canGoNextPurchaseOrder"
                        v-b-tooltip.hover
                        title="Orden siguiente"
                        @click="goNextPurchaseOrder()">
                <AppIcon name="chevron-down" />
              </b-button>
            </div>
          </div>

          <div class="list-view-drawer__stats">
            <div class="list-view-drawer__stat">
              <span class="list-view-drawer__stat-label">Total estimado</span>
              <span class="list-view-drawer__stat-value">
                {{Intl.NumberFormat('es-AR',{style:'currency',currency:'ARS'}).format(itemSelected.amount_total || 0)}}
              </span>
            </div>
            <div class="list-view-drawer__stat" :class="{ 'list-view-drawer__stat--warning': parseFloat(itemSelected.quantity_pending || 0) > 0 }">
              <span class="list-view-drawer__stat-label">Pendiente</span>
              <span class="list-view-drawer__stat-value">
                {{parseFloat(itemSelected.quantity_pending || 0).toFixed(2)}}
              </span>
            </div>
          </div>

          <div>
            <div class="list-view-drawer__section-title">
              <AppIcon name="list-ul" /> Detalle
            </div>

            <b-table class="app-table app-table--compact"
                    responsive
                    stacked
                    :hover="true"
                    :small="true"
                    :fixed="false"
                    :items="tableSub.items"
                    :fields="tableSub.fields"
                    v-if="tableSub.items.length">

              <template v-slot:cell(quantity)="row">
                {{parseFloat(row.item.quantity || 0).toFixed(2)}}
                <span v-if="row.item.unit_measurement">{{row.item.unit_measurement.reference}}</span>
              </template>

              <template v-slot:cell(product)="row">
                <div class="text-truncate" v-b-tooltip.hover :title="getProductName(row.item)">
                  {{getProductName(row.item)}}
                </div>
              </template>
            </b-table>
            <EmptyState v-else icon="list-ul" title="Sin items cargados" />
          </div>

          <b-button v-if="tableSub.items.length"
                    block
                    size="sm"
                    variant="outline-secondary"
                    class="text-left"
                    @click="openDetail()">
            <AppIcon name="visibility" class="mr-1" /> Ver detalle completo
          </b-button>

          <div class="list-view-drawer__actions">
            <b-button size="sm" variant="outline-secondary" @click="sendPrinter(itemSelected)">
              <AppIcon name="print" class="mr-1" /> Imprimir
            </b-button>
            <b-button size="sm" variant="outline-secondary" @click="edit(itemSelected)" v-if="canEditPurchaseOrder(itemSelected)">
              <AppIcon name="edit" class="mr-1" /> Editar
            </b-button>
            <b-button size="sm" variant="outline-secondary" @click="updatePurchaseOrderStatus(itemSelected, 'cancelled')" v-if="canCancelPurchaseOrder(itemSelected)">
              <AppIcon name="cancel" class="mr-1" /> Cancelar
            </b-button>
          </div>
        </div>

        <template #footer>
          <div class="list-view-drawer__footer" v-if="itemSelected && canDeletePurchaseOrder(itemSelected)">
            <b-button size="sm" variant="outline-danger" block @click="remove(itemSelected)">
              <AppIcon name="delete" class="mr-1" /> Eliminar
            </b-button>
          </div>
        </template>
      </b-sidebar>

      <b-row>
        <b-col lg="12" id="view_footer_purchase_orders">
          <b-card body-class="py-2">
            <div class="list-view-footer">
              <b-button type="button" variant="outline-dark" size="sm" @click="$router.go(-1)">
                <AppIcon name="back" />
                Volver
              </b-button>

              <nav v-if="table.tablePaginate">
                <b-pagination class="mb-0"
                              size="sm"
                              pills=""
                              :total-rows="table.tablePaginate.total"
                              :per-page="table.tablePaginate.per_page"
                              v-model="table.currentPage"
                              @input="filterPurchaseOrders()" />
              </nav>
            </div>
          </b-card>
        </b-col>
      </b-row>

      <Printer v-if="crud.print.id"
              :reference="crud.print.reference"
              :registerID="crud.print.id"
              :key="crud.print.key"/>

      <b-modal v-model="modal.form.active"
              no-close-on-esc
              no-close-on-backdrop
              hide-header-close
              size="xl"
              dialog-class="purchase-orders-form-modal-dialog"
              :key="modal.form._id"
              :id="modal.form._id">
        <div slot="modal-header" minimize class="app-modal-header">
          <span class="app-modal-header__icon"><AppIcon name="edit" /></span>
          <h5 class="app-modal-header__title">{{modal.form.title}}</h5>
        </div>

        <form-wizard title=""
                    subtitle=""
                    nextButtonText="Siguiente"
                    backButtonText="Atrás"
                    finishButtonText="Guardar"
                    color="#2f353a"
                    shape="tab"
                    @on-complete="save"
                    class="crud-sales-wizard">
          <tab-content title="Cabecera" icon="fa fa-edit" :before-change="purchaseOrderValidHeader">
            <div class="purchase-orders-form-section">
              <div class="purchase-orders-form-section__title">
                <AppIcon name="description" /> Datos generales
              </div>
              <b-row>
                <b-col md="4">
                  <b-form-group label="Fecha">
                  <b-form-datepicker size="sm" v-model="crud.form.date" placeholder="Fecha" local="es"></b-form-datepicker>
                  </b-form-group>
                </b-col>
                <b-col md="4">
                  <b-form-group label="Entrega esperada">
                  <b-form-datepicker size="sm" v-model="crud.form.date_expected" placeholder="Entrega esperada" local="es"></b-form-datepicker>
                  </b-form-group>
                </b-col>
              </b-row>
              <b-row>
                <b-col md="4">
                  <FindObject type="suppliers"
                              render="search"
                              @select-object="loadSuppliers($event)"
                              :valueID="crud.form.suppliers_id"
                              :where="conditionSuppliersActive"
                              :key="'finder_suppliers_' + componentKey" />
                </b-col>
              </b-row>
            </div>

          </tab-content>

          <tab-content title="Sugerencias" icon="fa fa-lightbulb-o" :before-change="purchaseOrderValidSuggestions">
            <div class="purchase-orders-form-section">
              <div class="purchase-orders-form-section__title">
                <AppIcon name="lightbulb" /> Sugerencias inteligentes
                <b-badge v-if="crud.form.suppliers" variant="light" class="purchase-orders-supplier-badge">
                  <AppIcon name="storefront" class="mr-1" />{{crud.form.suppliers.name}}
                </b-badge>
              </div>
              <b-row class="align-items-end">
              <b-col md="3">
                <b-form-group label="Consulta" label-size="sm">
                  <b-form-select size="sm"
                                v-model="suggestions.metric"
                                :disabled="isSuggestionsAutoLoading"
                                :options="arr.select.suggestionMetrics"></b-form-select>
                </b-form-group>
              </b-col>
              <b-col md="4">
                <b-form-group label="Buscar" label-size="sm">
                  <b-form-input size="sm"
                                v-model="suggestions.search"
                                :disabled="isSuggestionsAutoLoading"
                                placeholder="Buscar por código o nombre de producto"
                                @keyup.enter="loadSuggestions()"></b-form-input>
                </b-form-group>
              </b-col>
              <b-col md="2">
                <b-form-group label="Acciones" label-size="sm" label-class="invisible">
                  <b-form-checkbox switch
                                   class="text-nowrap pt-1"
                                   v-model="suggestions.includeSalesHistory"
                                   @change="onSuggestionsSalesHistoryToggle"
                                   :disabled="isSuggestionsAutoLoading">
                    Histórico de Venta
                  </b-form-checkbox>
                </b-form-group>
              </b-col>
              <b-col md="3" class="text-md-right">
                <b-form-group label="Acciones" label-size="sm" label-class="invisible">
                  <b-button variant="outline-dark" size="sm" @click="loadSuggestions()" :disabled="isSuggestionsAutoLoading" title="Actualizar sugerencias">
                    <AppIcon name="bolt" class="mr-1" /> Actualizar
                  </b-button>
                </b-form-group>
              </b-col>
              </b-row>

            <b-row class="mt-2 align-items-center">
              <b-col md="6" class="mb-2">
                <b-form-checkbox switch
                                 class="text-nowrap"
                                 v-model="suggestions.showPriceWithIva">
                  Precio de compra con IVA
                </b-form-checkbox>
              </b-col>
              <b-col md="6" class="mb-2 text-right">
                <small class="text-muted">
                  Mostrando <b>{{getSuggestionsVisibleCount}}</b> de <b>{{getSuggestionsLoadedCount}}</b> resultados
                </small>
              </b-col>
              <b-col md="12">
                <div class="suggestions-scroll" ref="suggestionsScroll" @scroll.passive="onSuggestionsScroll" v-if="getSuggestionsFiltered.length">
                  <b-table :class="['app-table app-table--compact table-purchase-orders-suggestions', {'table-suggestions-with-sold': showSoldSinceLastRemitColumn}]"
                        :hover="true"
                        :small="true"
                        :fixed="false"
                        :items="getSuggestionsFiltered"
                        :fields="getSuggestionTableFields"
                        :tbody-tr-class="getSuggestionRowClass">
                  <template v-slot:cell(product)="data">
                    <div class="suggestion-product-cell">
                      <b-badge :variant="getSuggestionCodeVariant(data.item)" class="suggestion-code">
                        {{data.item.product.code}}
                      </b-badge>
                      <div class="suggestion-name suggestion-name-clamp"
                           :title="data.item.product.name">
                        {{data.item.product.name}}
                      </div>
                      <div class="mt-1" v-if="isStockBelowMinimum(data.item) || hasPendingWithoutStock(data.item)">
                        <b-badge v-if="isStockBelowMinimum(data.item)" variant="warning" class="mr-1">
                          <AppIcon name="warning" /> Debajo del mínimo
                        </b-badge>
                        <b-badge v-if="hasPendingWithoutStock(data.item)" variant="danger">
                          <AppIcon name="cancel" /> Pendiente sin stock
                        </b-badge>
                      </div>
                    </div>
                  </template>

                  <template v-slot:cell(reason)="data">
                    <div class="mb-1" v-if="data.item.metrics && data.item.metrics.length">
                      <b-badge v-for="(metric, metricIndex) in data.item.metrics"
                              :key="'metric_' + (data.item.product ? data.item.product.id : 'x') + '_' + (metric || 'na') + '_' + metricIndex"
                              :variant="getMetricVariant(metric)"
                              class="mr-1">
                        {{getMetricLabel(metric)}}
                      </b-badge>
                    </div>
                    <div v-if="data.item.reasons && data.item.reasons.length">
                      <small v-for="(reason, idx) in data.item.reasons" :key="idx" class="d-block text-muted">
                        {{reason}}
                      </small>
                    </div>
                  </template>

                  <template v-slot:head(stock)>
                    <span title="Stock actual" class="text-nowrap">
                      <AppIcon name="inventory" class="mr-1" />Stk.
                    </span>
                  </template>
                  <template v-slot:head(sales_two_years)>
                    <span title="Vendido neto por año (año actual y anterior)" class="text-nowrap">
                      <AppIcon name="bar-chart" class="mr-1" />2A
                    </span>
                  </template>

                  <template v-slot:head(stock_pending)>
                    <span title="Stock comprometido" class="text-nowrap">
                      <AppIcon name="hourglass" class="mr-1" />Comp.
                    </span>
                  </template>

                  <template v-slot:head(stock_minimum)>
                    <span title="Stock mínimo" class="text-nowrap">
                      <AppIcon name="arrow-upward" class="mr-1" />Mín.
                    </span>
                  </template>

                  <template v-slot:head(stock_maximum)>
                    <span title="Stock máximo" class="text-nowrap">
                      <AppIcon name="arrow-downward" class="mr-1" />Máx.
                    </span>
                  </template>

                  <template v-slot:head(suggested_to_max)>
                    <span title="Cantidad sugerida para llegar al stock máximo (Máx. - Stock actual)" class="text-nowrap">
                      <AppIcon name="add_shopping_cart" class="mr-1" />Sugerido
                    </span>
                  </template>

                  <template v-slot:head(sold_period_quantity)>
                    <span title="Cantidad vendida desde el último remito del proveedor hasta hoy" class="text-nowrap">
                      <AppIcon name="show_chart" class="mr-1" />Vend
                    </span>
                  </template>

                  <template v-slot:cell(stock)="data">
                    <span :title="getSuggestionValueWithUnit(data.item, data.item.stock, 'Stock actual')"
                          :class="getStockCellTextClass(data.item)">
                      {{toFloat(data.item.stock).toFixed(2)}}
                    </span>
                  </template>
                  <template v-slot:cell(sales_two_years)="data">
                    <div class="suggestion-sales-history text-right"
                         :title="getSalesYearlyTooltip(data.item)">
                      <small class="d-block text-muted"
                             v-for="row in getSalesYearlyRows(data.item)"
                             :key="'sales_year_' + row.year + '_' + (data.item.product ? data.item.product.id : 'x')">
                        <span class="suggestion-sales-year">{{row.year}}</span>: {{toFloat(row.quantity).toFixed(2)}}
                      </small>
                      <small class="d-block text-muted" v-if="!getSalesYearlyRows(data.item).length">-</small>
                    </div>
                  </template>

                  <template v-slot:cell(stock_pending)="data">
                    <span :title="getSuggestionValueWithUnit(data.item, data.item.stock_pending, 'Stock comprometido')"
                          :class="{'text-danger': hasPendingWithoutStock(data.item)}">
                      {{toFloat(data.item.stock_pending).toFixed(2)}}
                    </span>
                  </template>

                  <template v-slot:cell(stock_minimum)="data">
                    <span :title="getSuggestionValueWithUnit(data.item, data.item.stock_minimum, 'Stock mínimo')">
                      {{toFloat(data.item.stock_minimum).toFixed(2)}}
                    </span>
                  </template>

                  <template v-slot:cell(stock_maximum)="data">
                    <span :title="getSuggestionValueWithUnit(data.item, data.item.stock_maximum, 'Stock máximo')"
                          :class="{'text-muted': !data.item.stock_maximum}">
                      {{data.item.stock_maximum ? toFloat(data.item.stock_maximum).toFixed(2) : '-'}}
                    </span>
                  </template>

                  <template v-slot:cell(suggested_to_max)="data">
                    <span v-if="data.item.stock_maximum > 0"
                          :title="getSuggestionValueWithUnit(data.item, Math.max(0, data.item.stock_maximum - data.item.stock), 'Sugerido para llegar al máximo')"
                          :class="{'text-success font-weight-bold': Math.max(0, data.item.stock_maximum - data.item.stock) > 0}">
                      {{Math.max(0, toFloat(data.item.stock_maximum) - toFloat(data.item.stock)).toFixed(2)}}
                    </span>
                    <span v-else class="text-muted">-</span>
                  </template>

                  <template v-slot:cell(sold_period_quantity)="data">
                    <span :title="getSoldSinceLastRemitTooltip(data.item)">
                      {{toFloat(data.item.quantity_suggested).toFixed(2)}}
                    </span>
                  </template>

                  <template v-slot:cell(purchase_price)="data">
                    <div class="suggestion-purchase-price">
                      <span>
                        {{Intl.NumberFormat('es-AR',{style:'currency',currency:'ARS'}).format(getSuggestionPurchasePrice(data.item))}}
                      </span>
                      <small class="suggestion-purchase-price-other d-block text-warning"
                             v-if="isSuggestionPurchasePriceFromOtherSupplier(data.item)"
                             v-b-tooltip.hover.left
                             :title="'Precio de compra por default, del proveedor ' + getSuggestionPurchasePriceSupplierName(data.item) + ' (no del proveedor seleccionado)'">
                        <AppIcon name="warning" /> {{getSuggestionPurchasePriceSupplierName(data.item)}}
                      </small>
                    </div>
                  </template>

                  <template v-slot:cell(quantity_draft)="data">
                    <b-input-group size="sm"
                                  class="suggestion-qty-group"
                                  :append="getUnitMeasurementReference(data.item)"
                                  :title="getUnitMeasurementName(data.item)">
                      <b-form-input type="number"
                                    step="1"
                                    min="0"
                                    size="sm"
                                    :class="{'suggestion-qty-selected': parseFloat(data.item.quantity_draft || 0) > 0}"
                                    v-model.number="data.item.quantity_draft"
                                    @focus="selectInputText"
                                    @input="onSuggestionQuantityInput(data.item)"></b-form-input>
                    </b-input-group>
                  </template>
                  </b-table>
                  <b-alert variant="info" show class="mb-0 mt-2 py-1 suggestions-loading-alert" v-if="suggestions.loadingMode === 'append'">
                    <b-spinner small class="mr-1"></b-spinner>
                    Cargando más sugerencias...
                  </b-alert>
                </div>
                <b-alert v-else variant="info" show>Sin sugerencias para mostrar</b-alert>
              </b-col>
            </b-row>
            </div>
          </tab-content>

          <tab-content title="Detalle" icon="fa fa-list" :before-change="purchaseOrderValidDetail">
            <div class="purchase-orders-form-section">
              <div class="purchase-orders-form-section__title">
                <AppIcon name="list-ul" /> Productos cargados
              </div>
            <b-row>
              <b-col md="12" class="mt-2">
                <div class="detail-table-scroll" v-if="crud.form.detailProducts.length">
                  <b-table class="app-table app-table--compact table-purchase-orders-detail"
                          responsive
                          :stacked="isMobileViewport ? 'md' : false"
                          :hover="true"
                          :small="true"
                          :fixed="false"
                          :items="crud.form.detailProducts"
                          :fields="tableDetail.fields">
                    <template v-slot:table-colgroup="scope">
                      <col
                        v-for="field in scope.fields"
                        :key="field.key"
                        :style="{ width: field.width }">
                    </template>

                    <template v-slot:cell(product)="data">
                      <div class="suggestion-product-cell">
                        <b-badge :variant="getSuggestionCodeVariant(data.item)" class="suggestion-code">
                          {{data.item.product ? data.item.product.code : ''}}
                        </b-badge>
                        <div class="suggestion-name">{{data.item.product ? data.item.product.name : ''}}</div>
                      </div>
                    </template>

                    <template v-slot:cell(origin)="data">
                      <div>
                        <b-badge v-for="(metric, metricIndex) in getDetailOriginMetrics(data.item)"
                                :key="'detail_metric_' + (data.item.product ? data.item.product.id : 'x') + '_' + (metric || 'na') + '_' + metricIndex"
                                :variant="getMetricVariant(metric)"
                                class="mr-1">
                          {{getMetricLabel(metric)}}
                        </b-badge>
                      </div>
                    </template>

                    <template v-slot:cell(quantity)="data">
                      <b-input-group size="sm"
                                    class="suggestion-qty-group"
                                    :append="getUnitMeasurementReference(data.item)"
                                    :title="getUnitMeasurementName(data.item)">
                        <b-form-input type="number" step="1" min="0" v-model.number="data.item.quantity" size="sm" @focus="selectInputText"></b-form-input>
                      </b-input-group>
                    </template>

                    <template v-slot:cell(price_cost)="data">
                      <span class="detail-price-readonly">
                        {{Intl.NumberFormat('es-AR',{style:'currency',currency:'ARS'}).format(parseFloat(data.item.price_cost || 0))}}
                      </span>
                    </template>

                    <template v-slot:cell(total)="data">
                      {{Intl.NumberFormat('es-AR',{style:'currency',currency:'ARS'}).format((parseFloat(data.item.quantity)||0) * (parseFloat(data.item.price_cost)||0))}}
                    </template>

                    <template v-slot:cell(f_action)="data">
                      <b-button variant="link" class="list-view-icon-btn" size="sm" @click="removeDetail(data.index)" v-b-tooltip.hover title="Eliminar item">
                        <AppIcon name="delete" class="text-danger" />
                      </b-button>
                    </template>
                  </b-table>
                </div>
                <EmptyState v-else icon="list-ul" title="No se cargaron items" description="Usá la pestaña Sugerencias para definir las cantidades a pedir." />

                <b-row class="mt-2">
                  <b-col md="12">
                    <div class="detail-summary-box">
                      <div class="detail-summary-left">
                        Productos: <b>{{getDetailProductsCount}}</b>
                      </div>
                      <div class="detail-summary-right">
                        Total estimado: <b>{{Intl.NumberFormat('es-AR',{style:'currency',currency:'ARS'}).format(getTotalDetailAmount || 0)}}</b>
                      </div>
                    </div>
                  </b-col>
                </b-row>
              </b-col>
            </b-row>
            </div>
          </tab-content>

          <tab-content title="Observaciones" icon="fa fa-flag-checkered">
            <div class="purchase-orders-form-section">
              <div class="purchase-orders-form-section__title">
                <AppIcon name="chat-square-text" /> Observaciones
              </div>
            <b-row>
              <b-col md="12">
                <b-form-group label="Observaciones">
                  <app-textarea rows="4" max-rows="8" v-model="crud.form.observations"></app-textarea>
                </b-form-group>
              </b-col>
            </b-row>
            </div>
          </tab-content>
        </form-wizard>

        <div slot="modal-footer">
          <b-button variant="outline-secondary" class="mr-1" @click="modal.form.active=false">
            <AppIcon name="x" class="mr-1" />Cancelar
          </b-button>
        </div>
      </b-modal>

      <b-modal v-model="modal.detail.active"
              size="xl"
              :key="modal.detail._id"
              :id="modal.detail._id">
        <div slot="modal-header" minimize class="app-modal-header">
          <span class="app-modal-header__icon"><AppIcon name="visibility" /></span>
          <h5 class="app-modal-header__title">{{modal.detail.title}}</h5>
        </div>

        <b-row>
          <b-col sm="12">
            <b-tabs active-nav-item-class="font-weight-bold text-uppercase text-dark">
              <b-tab>
                <template #title>
                  <AppIcon name="description" class="mr-1" /> General
                </template>
                <div class="app-modal-detail-list" v-if="itemSelected">
                  <div class="app-modal-detail-list__row">
                    <span class="app-modal-detail-list__label">ID</span>
                    <span class="app-modal-detail-list__value"><b>#{{itemSelected.id}}</b></span>
                  </div>
                  <div class="app-modal-detail-list__row">
                    <span class="app-modal-detail-list__label">Comprobante</span>
                    <span class="app-modal-detail-list__value"><b>ORDEN DE COMPRA #{{itemSelected.id}}</b></span>
                  </div>
                  <div class="app-modal-detail-list__row" v-if="itemSelected.staff">
                    <span class="app-modal-detail-list__label">Emisor</span>
                    <span class="app-modal-detail-list__value">{{itemSelected.staff.name}}</span>
                  </div>
                  <div class="app-modal-detail-list__row">
                    <span class="app-modal-detail-list__label">Fecha</span>
                    <span class="app-modal-detail-list__value">{{moment(itemSelected.date).format('DD MMMM YYYY')}}</span>
                  </div>
                  <div class="app-modal-detail-list__row" v-if="itemSelected.date_expected">
                    <span class="app-modal-detail-list__label">Entrega esperada</span>
                    <span class="app-modal-detail-list__value">{{moment(itemSelected.date_expected).format('DD MMMM YYYY')}}</span>
                  </div>
                  <div class="app-modal-detail-list__row" v-if="itemSelected.supplier">
                    <span class="app-modal-detail-list__label">Proveedor</span>
                    <span class="app-modal-detail-list__value">{{itemSelected.supplier.name}}</span>
                  </div>
                  <div class="app-modal-detail-list__row">
                    <span class="app-modal-detail-list__label">Estado</span>
                    <span class="app-modal-detail-list__value">
                      <b-badge :variant="getStatusVariant(itemSelected.status)">
                        {{itemSelected.status_label || getStatusLabel(itemSelected.status)}}
                      </b-badge>
                    </span>
                  </div>
                  <div class="app-modal-detail-list__row">
                    <span class="app-modal-detail-list__label">Remitos vinculados</span>
                    <span class="app-modal-detail-list__value">{{getDetailLinkedRemits.length}}</span>
                  </div>
                  <div class="app-modal-detail-list__row">
                    <span class="app-modal-detail-list__label">Cant. total</span>
                    <span class="app-modal-detail-list__value">{{parseFloat(itemSelected.quantity_total || 0).toFixed(2)}}</span>
                  </div>
                  <div class="app-modal-detail-list__row">
                    <span class="app-modal-detail-list__label">Cant. entregada</span>
                    <span class="app-modal-detail-list__value">{{parseFloat(itemSelected.quantity_delivered || 0).toFixed(2)}}</span>
                  </div>
                  <div class="app-modal-detail-list__row">
                    <span class="app-modal-detail-list__label">Cant. pendiente</span>
                    <span class="app-modal-detail-list__value app-modal-detail-list__value--warning">{{parseFloat(itemSelected.quantity_pending || 0).toFixed(2)}}</span>
                  </div>
                  <div class="app-modal-detail-list__row">
                    <span class="app-modal-detail-list__label">Total estimado</span>
                    <span class="app-modal-detail-list__value"><b>{{Intl.NumberFormat('es-AR',{style:'currency',currency:'ARS'}).format(itemSelected.amount_total || 0)}}</b></span>
                  </div>
                  <div class="app-modal-detail-list__row" v-if="itemSelected.observations">
                    <span class="app-modal-detail-list__label">Observaciones</span>
                    <span class="app-modal-detail-list__value">{{itemSelected.observations}}</span>
                  </div>
                </div>
              </b-tab>

              <b-tab>
                <template #title>
                  <AppIcon name="list-ul" class="mr-1" /> Items
                </template>
                <b-table class="app-table app-table--compact table-full-detail-purchase-orders"
                        responsive
                        :stacked="isMobileViewport ? 'md' : false"
                        :hover="true"
                        :small="true"
                        :fixed="true"
                        :items="tableDetail.items"
                        :fields="tableDetailModal.fields"
                        v-if="tableDetail.items.length">

                  <template v-slot:table-colgroup="scope">
                    <col
                      v-for="field in scope.fields"
                      :key="field.key"
                      :style="{ width: field.width }">
                  </template>

                  <template v-slot:cell(code)="data">
                    {{data.item.products_code || getProductCode(data.item)}}
                  </template>

                  <template v-slot:cell(product)="data">
                    <div class="purchase-orders-detail-item">
                      {{getProductName(data.item)}}
                    </div>
                  </template>

                  <template v-slot:cell(quantity)="data">
                    {{parseFloat(data.item.quantity || 0).toFixed(2)}} {{getUnitMeasurementReference(data.item)}}
                  </template>

                  <template v-slot:cell(quantity_delivered)="data">
                    {{parseFloat(data.item.quantity_delivered || 0).toFixed(2)}} {{getUnitMeasurementReference(data.item)}}
                  </template>

                  <template v-slot:cell(quantity_pending)="data">
                    <span :class="data.item.hidden_suggestion ? 'text-muted' : 'text-danger'" v-if="parseFloat(data.item.quantity_pending || 0) > 0">
                      {{parseFloat(data.item.quantity_pending || 0).toFixed(2)}} {{getUnitMeasurementReference(data.item)}}
                    </span>
                  </template>

                  <template v-slot:cell(price_cost)="data">
                    {{Intl.NumberFormat('es-AR',{style:'currency',currency:'ARS'}).format(data.item.price_cost || 0)}}
                  </template>

                  <template v-slot:cell(f_action)="data">
                    <b-link v-if="data.item.id && parseFloat(data.item.quantity_pending || 0) > 0"
                            class="purchase-orders-detail-action-link"
                            :title="data.item.hidden_suggestion ? 'Mostrar en sugerencias' : 'Ocultar de sugerencias'"
                            @click="toggleHiddenSuggestion(data.item)">
                      <AppIcon :name="data.item.hidden_suggestion ? 'visibility' : 'visibility_off'" class="mr-1" />
                      <span v-if="isMobileViewport">{{data.item.hidden_suggestion ? 'Mostrar' : 'Ocultar'}}</span>
                    </b-link>
                  </template>
                </b-table>
                <EmptyState v-else icon="list-ul" title="No se encontraron items" />
              </b-tab>

              <b-tab v-if="getDetailLinkedRemits.length">
                <template #title>
                  <AppIcon name="paperclip" class="mr-1" /> Remitos
                </template>
                <b-table class="app-table app-table--compact table-full-detail-purchase-orders"
                        responsive
                        :stacked="isMobileViewport ? 'md' : false"
                        :hover="true"
                        :small="true"
                        :fixed="true"
                        :items="getDetailLinkedRemits"
                        :fields="tableLinkedRemits.fields">

                  <template v-slot:table-colgroup="scope">
                    <col
                      v-for="field in scope.fields"
                      :key="field.key"
                      :style="{ width: field.width }">
                  </template>

                  <template v-slot:cell(date)="data">
                    {{moment(data.item.date).format('DD/MM/YYYY')}}
                  </template>

                  <template v-slot:cell(reference)="data">
                    {{data.item.reference}}
                  </template>

                  <template v-slot:cell(items)="data">
                    {{parseInt(data.item.items || 0)}}
                  </template>

                  <template v-slot:cell(quantity)="data">
                    {{parseFloat(data.item.quantity || 0).toFixed(2)}}
                  </template>
                </b-table>
              </b-tab>
            </b-tabs>
          </b-col>
        </b-row>

        <div slot="modal-footer">
          <b-button variant="outline-secondary" class="mr-1" @click="modal.detail.active=false">
            <AppIcon name="x" class="mr-1" />Cerrar
          </b-button>
        </div>
      </b-modal>

    </b-col>
  </b-row>
</template>

<script>
  import serviceAPI from './services'
  import Error from '@/handler/error'
  import ErrorToken from '@/handler/errorToken'
  import Storage from '@/handler/storageSession'
  import Profiles from '@/config/profiles'
  import Modules from '@/config/modules'
  import Helper from '@/handler/helper'
  import Printer from '@/components/inc/printer/printer'
  import FindObject from '@/components/inc/find/findObject'
  import moment from 'moment'
  import {FormWizard, TabContent} from 'vue-form-wizard'
  import 'vue-form-wizard/dist/vue-form-wizard.min.css'
  import Function from '@/handler/funciones'
  import StorageLocal from '@/handler/storageLocal'
  import TabHeaderActions from '@/components/inc/tabs/TabHeaderActions'

  export default {
    components: {
      Printer,
      FindObject,
      FormWizard,
      TabContent,
      TabHeaderActions,
    },
    data: () => {
      return {
        access: {
          module_id: Modules.ORDENES_COMPRAS,
          profile_id: Profiles.PERSONAL,
          view_reference: 'purchaseOrders',
          elements: {
            deletePurchaseOrders: true,
          }
        },
        table: {
          items: [],
          fields: [],
          mostrarFiltros: true,
          currentPage: 1,
          perPage: 0,
          isBusy: false,
          tablePaginate: null,
          rowSelected: null,
        },
        tableSub: {
          items: [],
          fields: [],
        },
        tableDetail: {
          items: [],
          fields: [],
        },
        tableDetailModal: {
          fields: [],
        },
        tableSuggestions: {
          fields: [],
        },
        tableLinkedRemits: {
          fields: [],
        },
        crud: {
          print: {
            id: 0,
            reference: '',
            key: Math.random(),
          },
          form: {
            id: 0,
            date: '',
            date_expected: '',
            observations: '',
            staff_id: 0,
            suppliers_id: 0,
            suppliers: null,
            detailProducts: [],
            detailProductsCompound: [],
          },
        },
        modal: {
          form: {
            active: false,
            title: '',
            _id: '',
          },
          detail: {
            active: false,
            title: '',
            _id: '',
          },
        },
        filters: {
          date_start: '',
          date_end: '',
          suppliers: null,
          status: null,
          id: '',
        },
        suggestions: {
          search: '',
          metric: 'supplier_catalog',
          includeSalesHistory: true,
          showPriceWithIva: false,
          draftByKey: {},
          page: 1,
          perPage: 50,
          total: 0,
          lastPage: 1,
          hasMore: false,
          isLoading: false,
          loadingMode: '',
          requestId: 0,
        },
        arr: {
          filters: {
            suppliers: [],
            status: [
              { code: 'pending', label: 'Pendiente' },
              { code: 'partial', label: 'Entrega parcial' },
              { code: 'delivered', label: 'Entregado' },
              { code: 'cancelled', label: 'Cancelado' },
            ],
          },
          select: {
            suggestionMetrics: [
              { value: 'all', text: 'Todas las consultas' },
              { value: 'supplier_catalog', text: 'Catálogo del proveedor' },
              { value: 'last_purchases', text: 'Últimas compras' },
              { value: 'pending_previous', text: 'Pendientes anteriores' },
              { value: 'low_stock', text: 'Debajo de stock mínimo' },
              { value: 'replica_previous', text: 'Réplica compra anterior' },
              { value: 'sold_since_last_remit', text: 'Vendido desde último remito' },
            ],
          },
          suggestionsMerged: [],
        },
        itemSelected: null,
        componentKey: 0,
        filterIdTimer: null,
        detailDrawer: false,
        filtersOpen: false,
        isMobileViewport: false,
      }
    },
    created () {
      Helper.hasAccessView(this.access)
      this.access.elements.deletePurchaseOrders = Helper.hasAccessElement(this.access, 'delete_purchase_orders')
    },
    computed: {
      conditionSuppliersActive() {
        return [{field: 'active', condition: true}, {field: 'shipping', condition: false}]
      },
      getTotalDetailAmount() {
        var total = 0
        if(this.crud.form.detailProducts && this.crud.form.detailProducts.length) {
          this.crud.form.detailProducts.forEach((element) => {
            total = total + (parseFloat(element.quantity || 0) * parseFloat(element.price_cost || 0))
          })
        }
        return total
      },
      getDetailProductsCount() {
        return (this.crud.form.detailProducts || []).filter((element) => parseFloat(element.quantity || 0) > 0).length
      },
      getSuggestionsLoadedCount() {
        return parseInt(this.suggestions.total || 0)
      },
      getSuggestionsVisibleCount() {
        return (this.arr.suggestionsMerged || []).length
      },
      getSuggestionsFiltered() {
        return this.arr.suggestionsMerged || []
      },
      isSuggestionsAutoLoading() {
        return !!this.suggestions.isLoading && this.suggestions.loadingMode === 'append'
      },
      showSoldSinceLastRemitColumn() {
        return (this.suggestions.metric || 'all') === 'sold_since_last_remit'
      },
      showSalesHistoryColumn() {
        return !!this.suggestions.includeSalesHistory
      },
      getSuggestionTableFields() {
        return (this.tableSuggestions.fields || []).filter((field) => {
          if(field.key === 'sold_period_quantity' && !this.showSoldSinceLastRemitColumn) {
            return false
          }
          if(field.key === 'sales_two_years' && !this.showSalesHistoryColumn) {
            return false
          }
          return true
        })
      },
      getDetailLinkedRemits() {
        return this.buildLinkedRemits(this.itemSelected)
      },
      currentPurchaseOrderIndex() {
        if(!this.itemSelected) return -1
        return this.table.items.findIndex(item => item.id === this.itemSelected.id)
      },
      canGoPrevPurchaseOrder() {
        return this.currentPurchaseOrderIndex > 0
      },
      canGoNextPurchaseOrder() {
        return this.currentPurchaseOrderIndex >= 0 && this.currentPurchaseOrderIndex < this.table.items.length - 1
      },
    },
    mounted() {
      this.filtersOpen = StorageLocal.getValue('filters_open_crud_purchase_orders') == 'true'
      this.suggestions.includeSalesHistory = StorageLocal.getValue('suggestions_sales_history_crud_purchase_orders') !== 'false'
      this.suggestions.showPriceWithIva = StorageLocal.getValue('suggestions_price_with_iva_crud_purchase_orders') == 'true'

      this.mobileMql = window.matchMedia('(max-width: 767.98px)')
      this.isMobileViewport = this.mobileMql.matches
      this.mobileMql.addEventListener('change', this.updateMobileViewport)

      this.loadFieldsTable()
      this.filterLoad()
      this.filterPurchaseOrders()

      // Acción directa desde la paleta de comandos: abrir el alta al entrar.
      if (this.$route.query.new == '1') {
        this.add()
        // Limpia el query apenas se consume -- si no, la pestaña queda con
        // ?new=1 pegado para siempre y volver a ella (activated, keep-alive)
        // reabriría el alta de nuevo aunque el usuario solo quiera ver el
        // listado.
        this.$router.replace({ path: this.$route.path, query: {} }).catch(() => {})
      }

      this._start = true
    },
    activated() {
      if(this._start) {
        this._start = false
        return false
      }

      this.filterLoad()
      this.filterPurchaseOrders()

      // Misma acción directa que en mounted(): si la pestaña ya estaba
      // montada (keep-alive), "mounted" no vuelve a correr y la paleta de
      // comandos no abriría nada sin este chequeo acá también.
      if (this.$route.query.new == '1') {
        this.add()
        // Limpia el query apenas se consume -- si no, la pestaña queda con
        // ?new=1 pegado para siempre y volver a ella (activated, keep-alive)
        // reabriría el alta de nuevo aunque el usuario solo quiera ver el
        // listado.
        this.$router.replace({ path: this.$route.path, query: {} }).catch(() => {})
      }
    },
    beforeDestroy() {
      if(this.filterIdTimer) {
        clearTimeout(this.filterIdTimer)
      }
      if(this.mobileMql) {
        this.mobileMql.removeEventListener('change', this.updateMobileViewport)
      }
    },
    watch: {
      filtersOpen(value) {
        StorageLocal.setValue('filters_open_crud_purchase_orders', value ? 'true' : 'false')
      },
      isMobileViewport() {
        this.loadFieldsTable()
      },
      'suggestions.includeSalesHistory'(value) {
        StorageLocal.setValue('suggestions_sales_history_crud_purchase_orders', value ? 'true' : 'false')
      },
      'suggestions.showPriceWithIva'(value) {
        StorageLocal.setValue('suggestions_price_with_iva_crud_purchase_orders', value ? 'true' : 'false')
      },
    },
    methods: {
      moment,

      loadFieldsTable() {
        this.table.fields = []
        this.tableSub.fields = []
        this.tableDetail.fields = []
        this.tableDetailModal.fields = []
        this.tableSuggestions.fields = []
        this.tableLinkedRemits.fields = []

        this.table.fields.push({key: 'voucher', label: 'Comprobante', class:'align-middle text-left', width:'240px'})
        this.table.fields.push({key: 'date', label: 'Fecha', class:'align-middle ' + this.alignClass('text-center'), width:'90px'})
        this.table.fields.push({key: 'suppliers_id', label: 'Proveedor', class:'align-middle', width:'180px'})
        this.table.fields.push({key: 'status', label: 'Estado', class:'align-middle ' + this.alignClass('text-center'), width:'110px'})
        this.table.fields.push({key: 'quantity_pending', label: 'Pendiente', class:'align-middle ' + this.alignClass('text-right'), width:'90px'})
        this.table.fields.push({key: 'amount_total', label: 'Total', class:'align-middle ' + this.alignClass('text-right'), width:'100px'})
        this.table.fields.push({key: 'f_action', label: this.isMobileViewport ? 'Acción' : '', class:'align-middle ' + this.alignClass('text-right'), width:'70px'})

        this.tableSub.fields.push({key: 'quantity', label: 'Cantidad', class:'align-middle', width:'70px'})
        this.tableSub.fields.push({key: 'product', label: 'Item', class:'align-middle', width:'230px'})

        this.tableDetail.fields.push({key: 'product', label: 'Item', class:'align-middle text-left', width:'290px'})
        this.tableDetail.fields.push({key: 'origin', label: 'Origen', class:'align-middle ' + this.alignClass('text-center'), width:'110px'})
        this.tableDetail.fields.push({key: 'quantity', label: 'Cantidad', class:'align-middle ' + this.alignClass('text-right'), width:'130px'})
        this.tableDetail.fields.push({key: 'price_cost', label: 'Costo', class:'align-middle ' + this.alignClass('text-right'), width:'110px'})
        this.tableDetail.fields.push({key: 'total', label: 'Total', class:'align-middle ' + this.alignClass('text-right'), width:'120px'})
        this.tableDetail.fields.push({key: 'f_action', label: this.isMobileViewport ? 'Acción' : '', class:'align-middle ' + this.alignClass('text-right'), width:'60px'})

        this.tableDetailModal.fields.push({key: 'code', label: 'Código', class:'align-middle text-left', width:'90px'})
        this.tableDetailModal.fields.push({key: 'product', label: 'Item', class:'align-middle text-left', width:'240px'})
        this.tableDetailModal.fields.push({key: 'quantity', label: 'Cant.', class:'align-middle ' + this.alignClass('text-right'), width:'70px'})
        this.tableDetailModal.fields.push({key: 'quantity_delivered', label: 'Entregado', class:'align-middle ' + this.alignClass('text-right'), width:'80px'})
        this.tableDetailModal.fields.push({key: 'quantity_pending', label: 'Pendiente', class:'align-middle ' + this.alignClass('text-right'), width:'80px'})
        this.tableDetailModal.fields.push({key: 'price_cost', label: 'Costo', class:'align-middle ' + this.alignClass('text-right'), width:'105px'})
        this.tableDetailModal.fields.push({key: 'f_action', label: this.isMobileViewport ? 'Acción' : '', class:'align-middle text-left', width:'78px'})

        this.tableSuggestions.fields.push({key: 'product', label: 'Producto', class:'align-middle text-left'})
        this.tableSuggestions.fields.push({key: 'reason', label: 'Criterios', class:'align-middle text-left'})
        this.tableSuggestions.fields.push({key: 'sales_two_years', label: 'Ventas 2A', class:'align-middle text-right', width:'110px'})
        this.tableSuggestions.fields.push({key: 'stock', label: 'Stk.', class:'align-middle text-right', width:'82px'})
        this.tableSuggestions.fields.push({key: 'stock_pending', label: 'Comp.', class:'align-middle text-right', width:'95px'})
        this.tableSuggestions.fields.push({key: 'stock_minimum', label: 'Mín.', class:'align-middle text-right', width:'82px'})
        this.tableSuggestions.fields.push({key: 'stock_maximum', label: 'Máx.', class:'align-middle text-right', width:'82px'})
        this.tableSuggestions.fields.push({key: 'suggested_to_max', label: 'Sugerido', class:'align-middle text-right', width:'92px'})
        this.tableSuggestions.fields.push({key: 'sold_period_quantity', label: 'Vend', class:'align-middle text-right', width:'92px'})
        this.tableSuggestions.fields.push({key: 'purchase_price', label: 'Precio compra', class:'align-middle text-right', width:'130px'})
        this.tableSuggestions.fields.push({key: 'quantity_draft', label: 'Cantidad', class:'align-middle text-right', width:'140px'})

        this.tableLinkedRemits.fields.push({key: 'date', label: 'Fecha', class:'align-middle text-center', width:'110px'})
        this.tableLinkedRemits.fields.push({key: 'reference', label: 'Comprobante', class:'align-middle text-left'})
        this.tableLinkedRemits.fields.push({key: 'supplier', label: 'Proveedor', class:'align-middle text-left'})
        this.tableLinkedRemits.fields.push({key: 'items', label: 'Items', class:'align-middle text-right', width:'90px'})
        this.tableLinkedRemits.fields.push({key: 'quantity', label: 'Cant. entregada', class:'align-middle text-right', width:'130px'})
      },
      updateMobileViewport(e) {
        this.isMobileViewport = e.matches
      },
      alignClass(desktopClass) {
        return this.isMobileViewport ? 'text-left' : desktopClass
      },
      avatarFromName(name) {
        return Function.avatarFromName(name)
      },
      selectInputText(event) {
        event.target.select()
      },
      confirmDialog(message, options) {
        var danger = !!options.danger
        var icon = options.icon || (danger ? 'warning' : 'help')
        var title = this.$createElement('span', { class: 'app-confirm-header__title' }, [
          this.$createElement('AppIcon', { props: { name: icon } }),
          options.title
        ])
        return this.$bvModal.msgBoxConfirm(message, {
          title: title,
          size: options.size || 'sm',
          buttonSize: options.buttonSize || 'sm',
          okVariant: danger ? 'danger' : 'dark',
          okTitle: options.okTitle,
          cancelTitle: options.cancelTitle || 'Cancelar',
          footerClass: 'p-2',
          headerClass: danger ? 'app-confirm-header app-confirm-header--danger' : 'app-confirm-header',
          hideHeaderClose: false,
          centered: true
        })
      },
      forceRender() {
        this.componentKey += 1
      },

      // FILTERS
      filterLoad() {
        this.filterLoadSuppliers()

        this.filters.date_start = moment().subtract(30, 'days').format('YYYY-MM-DD')
        this.filters.date_end = moment().format('YYYY-MM-DD')
        this.filters.id = ''

        this.restoreFilterStorage()
      },
      onInputIdFilter() {
        var onlyNumbers = (this.filters.id || '').toString().replace(/[^0-9]/g, '')
        if(onlyNumbers !== this.filters.id) {
          this.filters.id = onlyNumbers
        }

        if(this.filterIdTimer) {
          clearTimeout(this.filterIdTimer)
        }

        this.filterIdTimer = setTimeout(() => {
          this.filterPurchaseOrders(true)
        }, 350)
      },
      getFilterOptionKey(option) {
        if(option && option.code !== undefined && option.code !== null && option.code !== '') {
          return String(option.code)
        }

        if(option && option.label) {
          return String(option.label)
        }

        return 'option-empty'
      },
      filterLoadSuppliers() {
        serviceAPI.obtenerProveedores()
        .then((response) => {
          var data = response.data
          this.arr.filters.suppliers = []
          var added = {}

          data.forEach((element) => {
            if(!element || !element.id) {
              return
            }

            if(added[element.id]) {
              return
            }
            added[element.id] = true

            var label = ''
            if(element.name) {
              label = element.name.toString().trim()
            }

            this.arr.filters.suppliers.push({
              code: element.id,
              label: label ? label : ('Proveedor #' + element.id),
            })
          })
        })
        .catch(error => {
          this.$awn.alert(Error.showError(error))
        })
      },
      filterPurchaseOrders(resetPage = false) {
        if(resetPage) {
          this.table.currentPage = 1
        }

        this.saveFilterStorage()
        this.table.isBusy = true

        serviceAPI.filtrarOrdenes(this.filters, this.table.currentPage)
        .then((response) => {
          var data = response.data
          this.table.tablePaginate = data
          this.table.items = data.data

          this.table.isBusy = false

          if(this.itemSelected) {
            this.table.items.forEach((element) => {
              if(element.id === this.itemSelected.id) {
                this.openSub(element)
              }
            })
          }
        })
        .catch(error => {
          this.table.isBusy = false
          this.$awn.alert(ErrorToken.valid(error))
        })
      },
      saveFilterStorage() {
        Storage.setValue('filter_crud_purchase_orders', JSON.stringify(this.filters))
      },
      restoreFilterStorage() {
        if(Storage.getValue('filter_crud_purchase_orders')) {
          var data = JSON.parse(Storage.getValue('filter_crud_purchase_orders'))
          this.filters = Object.assign({}, this.filters, data)
        }

        if(this.filters.id === null || this.filters.id === undefined) {
          this.filters.id = ''
        } else {
          this.filters.id = this.filters.id.toString().replace(/[^0-9]/g, '')
        }
      },

      // TABLE / DETAIL
      onRowSelected(item) {
        if(item.length) {
          this.openSub(item[0])
        }
      },
      onRowClicked(item, index, event) {
        if(!item) return

        if(event && event.target) {
          const ignoreTags = ['INPUT', 'BUTTON', 'A', 'SELECT', 'TEXTAREA', 'LABEL', 'SVG', 'PATH']
          if(ignoreTags.includes(event.target.tagName)) return
          if(event.target.closest('.dropdown')) return
        }

        this.openSub(item)
      },
      rowClass(item, type) {
        if(type !== 'row' || !item) return ''
        if(this.detailDrawer && this.itemSelected && item.id === this.itemSelected.id) {
          return 'app-table-row-active'
        }
        return ''
      },
      goPrevPurchaseOrder() {
        if(!this.canGoPrevPurchaseOrder) return
        this.openSub(this.table.items[this.currentPurchaseOrderIndex - 1])
      },
      goNextPurchaseOrder() {
        if(!this.canGoNextPurchaseOrder) return
        this.openSub(this.table.items[this.currentPurchaseOrderIndex + 1])
      },
      openSub(item) {
        this.itemSelected = item
        this.tableSub.items = item.details || []
        this.detailDrawer = true
      },
      hideSub() {
        this.itemSelected = null
        this.tableSub.items = []
        this.detailDrawer = false
      },
      openDetail(item = null) {
        var selected = item || this.itemSelected
        if(!selected) {
          return false
        }

        this.itemSelected = selected
        this.tableSub.items = this.itemSelected.details || []
        this.tableDetail.items = this.itemSelected.details || []

        this.modal.detail._id = Math.random().toString(36).slice(2, 8)
        this.modal.detail.title = 'Detalle de Orden de Compra'
        this.modal.detail.active = true

        let loader = this.$loading.show()
        serviceAPI.mostrarOrden(this.itemSelected.id)
        .then((response) => {
          var data = response.data
          this.itemSelected = data
          this.tableSub.items = data.details || []
          this.tableDetail.items = data.details || []
          loader.hide()
        })
        .catch(error => {
          loader.hide()
          this.$awn.alert(Error.showError(error))
        })
      },
      canCancelPurchaseOrder(item) {
        return item && item.status === 'pending'
      },
      canSetPendingPurchaseOrder(item) {
        return item && item.status === 'cancelled'
      },
      canShowStatusHeader(item) {
        return this.canCancelPurchaseOrder(item) || this.canSetPendingPurchaseOrder(item)
      },
      canEditPurchaseOrder(item) {
        return item && item.status === 'pending'
      },
      canDeletePurchaseOrder(item) {
        return item && this.access.elements.deletePurchaseOrders && item.status === 'pending'
      },
      canShowActionsHeader(item) {
        return this.canEditPurchaseOrder(item) || this.canDeletePurchaseOrder(item)
      },
      updatePurchaseOrderStatus(item, status) {
        if(!item || !item.id) {
          return false
        }

        var toLabel = status === 'cancelled' ? 'Cancelado' : 'Pendiente'
        var actionLabel = status === 'cancelled' ? 'cancelar' : 'pasar a pendiente'

        this.confirmDialog('¿Desea ' + actionLabel + ' la orden de compra seleccionada?', {
          title: 'Cambiar Estado',
          icon: status === 'cancelled' ? 'cancel' : 'undo',
          danger: status === 'cancelled',
          okTitle: 'SI',
          cancelTitle: 'NO'
        })
        .then((value) => {
          if(!value) {
            return
          }

          let loader = this.$loading.show()
          serviceAPI.actualizarEstadoOrden(item.id, { status: status })
          .then((response) => {
            loader.hide()
            var data = response.data

            if(this.itemSelected && parseInt(this.itemSelected.id) === parseInt(item.id)) {
              this.openSub(data)
            }

            this.filterPurchaseOrders()
            this.$awn.success('Estado actualizado a ' + toLabel)
          })
          .catch(error => {
            loader.hide()
            this.$awn.alert(Error.showError(error))
          })
        })
      },

      purchaseOrderValidHeader() {
        if(!this.crud.form.date) {
          this.$awn.alert('No se cargó la fecha')
          return false
        }
        if(!this.crud.form.suppliers_id) {
          this.$awn.alert('No se cargó el proveedor')
          return false
        }
        return true
      },
      purchaseOrderValidSuggestions() {
        return this.purchaseOrderValidHeader()
      },
      purchaseOrderValidDetail() {
        if(!this.crud.form.detailProducts.length) {
          this.$awn.alert('Debe cargar al menos un item')
          return false
        }

        var valid = true
        this.crud.form.detailProducts.forEach((element) => {
          if(!element.product || !element.unit_measurement || parseFloat(element.quantity) <= 0) {
            valid = false
          }
        })

        if(!valid) {
          this.$awn.alert('Hay items incompletos en el detalle')
          return false
        }

        return true
      },
      resetPurchaseOrderForm() {
        this.crud.form.id = 0
        this.crud.form.date = ''
        this.crud.form.date_expected = ''
        this.crud.form.observations = ''
        this.crud.form.staff_id = 0
        this.crud.form.suppliers_id = 0
        this.crud.form.suppliers = null
        this.crud.form.detailProducts = []
        this.crud.form.detailProductsCompound = []

        this.arr.suggestionsMerged = []
        this.suggestions.search = ''
        this.suggestions.metric = 'supplier_catalog'
        this.suggestions.draftByKey = {}
        this.resetSuggestionsPagination()
      },

      // CRUD MAIN
      add() {
        this.resetPurchaseOrderForm()

        this.crud.form.date = moment().format('YYYY-MM-DD')
        this.crud.form.date_expected = moment().add(7, 'days').format('YYYY-MM-DD')
        this.crud.form.staff_id = Helper.getStaffId()

        this.modal.form._id = Math.random().toString(36).slice(2, 8)
        this.modal.form.title = 'Nueva Orden de Compra'
        this.modal.form.active = true
      },
      edit(item) {
        if(item.status !== 'pending') {
          this.$awn.alert('Solo se pueden editar órdenes pendientes')
          return false
        }

        let loader = this.$loading.show()
        serviceAPI.mostrarOrden(item.id)
        .then((response) => {
          var data = response.data

          this.crud.form.id = data.id
          this.crud.form.date = data.date
          this.crud.form.date_expected = data.date_expected
          this.crud.form.observations = data.observations
          this.crud.form.staff_id = data.staff_id || Helper.getStaffId()

          this.crud.form.suppliers_id = data.suppliers_id
          this.crud.form.suppliers = data.supplier

          this.crud.form.detailProducts = this.normalizeDetailsForForm(data.details || [])
          this.crud.form.detailProductsCompound = []

          this.arr.suggestionsMerged = []
          this.suggestions.search = ''
          this.suggestions.metric = 'supplier_catalog'
          this.suggestions.draftByKey = this.getSuggestionDraftMapFromDetails()
          this.resetSuggestionsPagination()

          this.modal.form._id = Math.random().toString(36).slice(2, 8)
          this.modal.form.title = 'Editar Orden de Compra'
          this.modal.form.active = true

          loader.hide()
        })
        .catch(error => {
          loader.hide()
          this.$awn.alert(Error.showError(error))
        })
      },
      remove(item) {
        if(item.status !== 'pending') {
          this.$awn.alert('Solo se pueden eliminar órdenes pendientes')
          return false
        }

        this.confirmDialog('¿Desea borrar la orden de compra seleccionada?', {
          title: 'Borrar Orden de Compra',
          icon: 'delete',
          danger: true,
          okTitle: 'SI',
          cancelTitle: 'NO'
        })
        .then(value => {
          if(value) {
            let loader = this.$loading.show()
            serviceAPI.eliminarOrden(item.id)
            .then(() => {
              loader.hide()
              this.hideSub()
              this.filterPurchaseOrders()
              this.$awn.success('Orden eliminada')
            })
            .catch(error => {
              loader.hide()
              this.$awn.alert(Error.showError(error))
            })
          }
        })
      },
      save() {
        this.crud.form.detailProducts = (this.crud.form.detailProducts || []).filter((element) => {
          return parseFloat(element.quantity || 0) > 0
        })

        if(!this.validForm()) {
          return false
        }

        this.confirmDialog('¿Desea continuar con la generación de la orden de compra?', {
          title: 'Generar Orden de Compra',
          icon: 'save',
          okVariant: 'dark',
          okTitle: 'GENERAR',
          cancelTitle: 'Cancelar'
        })
        .then(value => {
          if(!value) {
            return
          }

          let payload = this.getPayloadForSave()
          let loader = this.$loading.show()
          var result = null
          if(this.crud.form.id) {
            result = serviceAPI.editarOrden(payload)
          } else {
            result = serviceAPI.agregarOrden(payload)
          }

          result.then((response) => {
            loader.hide()
            this.modal.form.active = false
            this.$awn.success('Orden guardada con éxito')

            var data = response.data

            this.resetPurchaseOrderForm()
            this.filterPurchaseOrders()

            if(data && data.id) {
              this.openSub(data)
            }
          })
          .catch(error => {
            loader.hide()
            this.$awn.alert(Error.showError(error))
          })
        })
      },
      validForm() {
        if(!this.crud.form.date) {
          this.$awn.alert('No se cargó la fecha')
          return false
        }

        if(!this.crud.form.suppliers_id) {
          this.$awn.alert('No se cargó el proveedor')
          return false
        }

        if(!this.crud.form.detailProducts.length) {
          this.$awn.alert('Debe cargar al menos un item')
          return false
        }

        var valid = true
        this.crud.form.detailProducts.forEach((element) => {
          if(!element.product || !element.unit_measurement || parseFloat(element.quantity) <= 0) {
            valid = false
          }
        })

        if(!valid) {
          this.$awn.alert('Hay items incompletos en el detalle')
          return false
        }

        return true
      },
      getPayloadForSave() {
        var payload = {
          id: this.crud.form.id,
          date: this.crud.form.date,
          date_expected: this.crud.form.date_expected,
          observations: this.crud.form.observations,
          staff_id: this.crud.form.staff_id || Helper.getStaffId(),
          suppliers_id: this.crud.form.suppliers_id,
          detailProducts: [],
          detailProductsCompound: [],
        }

        this.crud.form.detailProducts.forEach((element) => {
          payload.detailProducts.push({
            product: element.product,
            unit_measurement: element.unit_measurement,
            quantity: parseFloat(element.quantity || 0),
            price_cost: parseFloat(element.price_cost || 0),
            origin: element.origin || 'manual',
            origin_metadata: element.origin_metadata || null,
            observations: element.observations || '',
          })
        })

        return payload
      },

      // FLOW SUPPLIER
      loadSuppliers (object) {
        if(object) {
          if(this.crud.form.suppliers_id !== object.id) {
            this.forceRender()
            this.arr.suggestionsMerged = []
            this.suggestions.search = ''
            this.suggestions.metric = 'supplier_catalog'
            this.suggestions.draftByKey = {}
            this.resetSuggestionsPagination()
            this.resetSuggestionsScrollTop()
          }

          this.crud.form.suppliers = object
          this.crud.form.suppliers_id = object.id
        } else {
          this.crud.form.suppliers = null
          this.crud.form.suppliers_id = 0
          this.arr.suggestionsMerged = []
          this.suggestions.search = ''
          this.suggestions.metric = 'supplier_catalog'
          this.suggestions.draftByKey = {}
          this.resetSuggestionsPagination()
          this.resetSuggestionsScrollTop()
        }
      },

      getProductKey(product) {
        if(!product || !product.id) {
          return null
        }

        return parseInt(product.id) + ':' + (product.source || 'product')
      },
      getSuggestionKey(item) {
        return this.getProductKey(item ? item.product : null)
      },
      getSuggestionDraftMapFromDetails() {
        var map = {}
        ;(this.crud.form.detailProducts || []).forEach((element) => {
          var key = this.getProductKey(element.product)
          if(key) {
            map[key] = parseFloat(element.quantity || 0)
          }
        })
        return map
      },
      syncSuggestionDraftByKey(productKey, quantity) {
        if(!productKey) {
          return false
        }

        var qty = parseFloat(quantity || 0)
        if(qty > 0) {
          this.$set(this.suggestions.draftByKey, productKey, qty)
        } else if(Object.prototype.hasOwnProperty.call(this.suggestions.draftByKey, productKey)) {
          this.$delete(this.suggestions.draftByKey, productKey)
        }
      },
      resetSuggestionsPagination() {
        this.suggestions.page = 1
        this.suggestions.total = 0
        this.suggestions.lastPage = 1
        this.suggestions.hasMore = false
        this.suggestions.isLoading = false
        this.suggestions.loadingMode = ''
        this.suggestions.requestId = parseInt(this.suggestions.requestId || 0) + 1
      },
      onSuggestionsSalesHistoryToggle() {
        this.loadSuggestions({ append: false, showSupplierAlert: false })
      },
      onSuggestionsScroll(event) {
        if(!this.suggestions.hasMore || this.suggestions.isLoading) {
          return false
        }

        var target = event && event.target ? event.target : null
        if(!target) {
          return false
        }

        var maxScroll = target.scrollHeight - target.clientHeight
        if(maxScroll <= 0) {
          this.loadMoreSuggestions()
          return false
        }

        var scrollProgress = target.scrollTop / maxScroll
        if(scrollProgress >= 0.5) {
          this.loadMoreSuggestions()
        }
      },
      resetSuggestionsScrollTop() {
        this.$nextTick(() => {
          var contain = this.$refs.suggestionsScroll
          if(!contain) {
            return false
          }
          contain.scrollTop = 0
        })
      },
      triggerAutoLoadSuggestionsIfNeeded() {
        if(!this.suggestions.hasMore || this.suggestions.isLoading) {
          return false
        }

        this.$nextTick(() => {
          var contain = this.$refs.suggestionsScroll
          if(!contain) {
            return false
          }

          if((contain.scrollHeight - contain.clientHeight) <= 20) {
            this.loadMoreSuggestions()
          }
        })
      },
      findDetailIndexByProduct(product) {
        return this.crud.form.detailProducts.findIndex((element) => {
          if(!element.product || !product) {
            return false
          }

          return parseInt(element.product.id) === parseInt(product.id) &&
                 (element.product.source || 'product') === (product.source || 'product')
        })
      },

      upsertDetail(detail, mode = 'sum') {
        var index = this.findDetailIndexByProduct(detail.product)
        var detailQuantity = parseFloat(detail.quantity || 0)
        var productKey = this.getProductKey(detail.product)
        var finalQuantity = detailQuantity

        if(index >= 0) {
          if(mode === 'replace') {
            this.crud.form.detailProducts[index].quantity = detailQuantity
          } else {
            this.crud.form.detailProducts[index].quantity = parseFloat(this.crud.form.detailProducts[index].quantity || 0) + detailQuantity
          }

          if(parseFloat(this.crud.form.detailProducts[index].quantity || 0) <= 0) {
            this.crud.form.detailProducts.splice(index, 1)
            this.syncSuggestionDraftByKey(productKey, 0)
            return false
          }

          finalQuantity = parseFloat(this.crud.form.detailProducts[index].quantity || 0)

          if(parseFloat(detail.price_cost || 0) > 0) {
            this.crud.form.detailProducts[index].price_cost = parseFloat(detail.price_cost)
          }

          // Conservamos el primer origen y consolidamos métricas/reasons para trazabilidad.
          if(!this.crud.form.detailProducts[index].origin && detail.origin) {
            this.crud.form.detailProducts[index].origin = detail.origin
          }
          this.crud.form.detailProducts[index].origin_metadata = this.mergeOriginMetadata(
            this.crud.form.detailProducts[index].origin_metadata,
            detail.origin_metadata
          )
        } else {
          if(detailQuantity <= 0) {
            this.syncSuggestionDraftByKey(productKey, 0)
            return false
          }
          detail.origin_metadata = this.mergeOriginMetadata(null, detail.origin_metadata)
          this.crud.form.detailProducts.push(detail)
        }

        this.syncSuggestionDraftByKey(productKey, finalQuantity)
      },
      removeDetail(index) {
        var removed = this.crud.form.detailProducts[index]
        this.crud.form.detailProducts.splice(index, 1)
        this.syncSuggestionDraftByKey(this.getProductKey(removed ? removed.product : null), 0)
      },

      // SUGGESTIONS
      loadSuggestions(options = {}) {
        var append = !!options.append
        var showSupplierAlert = options.showSupplierAlert !== false

        if(!this.crud.form.suppliers_id) {
          if(showSupplierAlert) {
            this.$awn.alert('Debe cargar proveedor en cabecera para generar sugerencias')
          }
          return false
        }

        if(this.suggestions.isLoading) {
          return false
        }

        if(append && !this.suggestions.hasMore) {
          return false
        }

        var page = append ? (parseInt(this.suggestions.page || 1) + 1) : 1
        if(!append) {
          this.suggestions.page = 1
          this.suggestions.total = 0
          this.suggestions.lastPage = 1
          this.suggestions.hasMore = false
          this.resetSuggestionsScrollTop()
        }

        var requestId = parseInt(this.suggestions.requestId || 0) + 1
        this.suggestions.requestId = requestId
        this.suggestions.isLoading = true
        this.suggestions.loadingMode = append ? 'append' : 'initial'

        let loader = append ? null : this.$loading.show()
        let payload = {
          suppliers_id: this.crud.form.suppliers_id,
          purchase_orders_id: this.crud.form.id || 0,
          metric: this.suggestions.metric || 'all',
          include_sales_history: this.suggestions.includeSalesHistory ? 1 : 0,
          search: this.suggestions.search || '',
          page: page,
          per_page: this.suggestions.perPage || 50,
        }

        let result = null
        if(this.crud.form.id) {
          result = serviceAPI.obtenerSugerencias(this.crud.form.id, payload)
        } else {
          result = serviceAPI.obtenerSugerenciasPorProveedor(payload)
        }

        result
        .then((response) => {
          if(loader) {
            loader.hide()
          }
          if(requestId !== this.suggestions.requestId) {
            return false
          }

          var data = response.data
          var paginate = data.paginate || {}
          this.suggestions.draftByKey = {
            ...this.suggestions.draftByKey,
            ...this.getSuggestionDraftMapFromDetails(),
          }

          var rows = (data.merged || []).map((element) => {
            var key = this.getSuggestionKey(element)
            var quantityDraft = key && this.suggestions.draftByKey[key] ? parseFloat(this.suggestions.draftByKey[key]) : 0
            var metrics = Array.isArray(element.metrics) ? element.metrics.filter((metric) => !!metric) : []
            if(!metrics.length && element.metric) {
              metrics = [element.metric]
            }
            var reasons = Array.isArray(element.reasons) ? element.reasons.filter((reason) => !!reason) : []

            return {
              ...element,
              metrics: metrics,
              reasons: reasons,
              stock: this.toFloat(element.stock),
              stock_pending: this.toFloat(element.stock_pending),
              stock_minimum: this.toFloat(element.stock_minimum),
              stock_maximum: this.toFloat(element.stock_maximum),
              quantity_draft: quantityDraft,
            }
          })

          this.arr.suggestionsMerged = append ? (this.arr.suggestionsMerged || []).concat(rows) : rows
          this.suggestions.total = parseInt(paginate.total || this.arr.suggestionsMerged.length || 0)
          this.suggestions.page = parseInt(paginate.current_page || page || 1)
          this.suggestions.lastPage = parseInt(paginate.last_page || this.suggestions.page || 1)
          this.suggestions.hasMore = !!paginate.has_more
          this.suggestions.isLoading = false
          this.suggestions.loadingMode = ''

          if(!append) {
            this.resetSuggestionsScrollTop()
          }
          this.triggerAutoLoadSuggestionsIfNeeded()
        })
        .catch(error => {
          if(loader) {
            loader.hide()
          }
          if(requestId !== this.suggestions.requestId) {
            return false
          }
          this.suggestions.isLoading = false
          this.suggestions.loadingMode = ''
          this.$awn.alert(Error.showError(error))
        })
      },
      loadMoreSuggestions() {
        this.loadSuggestions({ append: true, showSupplierAlert: false })
      },
      buildDetailFromSuggestion(item, quantity) {
        if(!item.product) {
          return null
        }

        var product = JSON.parse(JSON.stringify(item.product))
        product.source = product.source || 'product'

        return {
          product: product,
          unit_measurement: item.unit_measurement || { id: 18, reference: 'UN', name: 'Unidad' },
          quantity: parseFloat(quantity || 0),
          price_cost: parseFloat(item.price_cost || 0),
          origin: item.metric || 'manual',
          origin_metadata: {
            metric: item.metric,
            metrics: item.metrics || (item.metric ? [item.metric] : []),
            reasons: item.reasons || [],
          },
          observations: '',
        }
      },
      onSuggestionQuantityInput(item) {
        if(!item || !item.product) {
          return false
        }

        var quantity = parseFloat(item.quantity_draft || 0)
        if(isNaN(quantity) || quantity < 0) {
          quantity = 0
        }

        item.quantity_draft = quantity
        this.syncSuggestionDraftByKey(this.getSuggestionKey(item), quantity)

        var detail = this.buildDetailFromSuggestion(item, quantity)
        if(!detail) {
          return false
        }

        this.upsertDetail(detail, 'replace')
      },
      sendPrinter(item) {
        if(!item || !item.id) {
          this.$awn.alert('No se encontró la orden de compra para imprimir')
          return false
        }

        this.crud.print.key = Math.random()
        this.crud.print.id = item.id
        this.crud.print.reference = item.reportDefault || 'purchase-order'
      },

      // HELPERS
      replaceItemInList(data) {
        if(!data || !data.id) {
          this.filterPurchaseOrders()
          return false
        }

        var index = this.table.items.findIndex((element) => element.id === data.id)
        if(index >= 0) {
          this.$set(this.table.items, index, data)
        } else {
          this.filterPurchaseOrders()
        }
      },
      normalizeDetailsForForm(details) {
        var data = []
        details.forEach((detail) => {
          var product = this.getProductFromDetail(detail)
          var unitMeasurement = detail.unit_measurement || (product ? (product.unit_measurement_purchase_default || product.unit_measurement_purchase) : null)

          data.push({
            id: detail.id,
            product: product,
            unit_measurement: unitMeasurement || { id: 18, reference: 'UN', name: 'Unidad' },
            quantity: parseFloat(detail.quantity || 0),
            quantity_delivered: parseFloat(detail.quantity_delivered || 0),
            quantity_pending: parseFloat(detail.quantity_pending || 0),
            price_cost: parseFloat(detail.price_cost || 0),
            origin: detail.origin || 'manual',
            origin_metadata: detail.origin_metadata || null,
            observations: detail.observations || '',
            products_code: detail.products_code,
            products_name: detail.products_name,
            hidden_suggestion: !!detail.hidden_suggestion,
          })
        })

        return data
      },
      getProductFromDetail(detail) {
        var product = detail.select_item || detail.select_product || detail.product

        if(!product) {
          product = {
            id: detail.products_id || detail.products_colors_id || detail.products_waist_id || detail.products_color_waist_id || detail.products_compound_id,
            code: detail.products_code,
            name: detail.products_name,
          }
        }

        if(detail.products_color_waist_id) {
          product.source = 'colors_waist'
        } else if(detail.products_colors_id) {
          product.source = 'colors'
        } else if(detail.products_waist_id) {
          product.source = 'waist'
        } else if(detail.products_compound_id) {
          product.source = 'compound'
        } else {
          product.source = product.source || 'product'
        }

        return product
      },
      getProductName(item) {
        if(item.products_name) {
          return item.products_name
        }

        if(item.product && item.product.name) {
          return item.product.name
        }

        if(item.select_item && item.select_item.name) {
          return item.select_item.name
        }

        if(item.select_product && item.select_product.name) {
          return item.select_product.name
        }

        return ''
      },
      getProductCode(item) {
        if(item.products_code) {
          return item.products_code
        }

        if(item.product && item.product.code) {
          return item.product.code
        }

        if(item.select_item && item.select_item.code) {
          return item.select_item.code
        }

        if(item.select_product && item.select_product.code) {
          return item.select_product.code
        }

        return ''
      },
      getUnitMeasurement(item) {
        var unitMeasurement = item && item.unit_measurement ? item.unit_measurement : null

        if(!unitMeasurement && item && item.product) {
          unitMeasurement = item.product.unit_measurement_purchase_default || item.product.unit_measurement_purchase || null
        }

        if(!unitMeasurement) {
          return { reference: 'UN', name: 'Unidad' }
        }

        return unitMeasurement
      },
      getUnitMeasurementReference(item) {
        var unitMeasurement = this.getUnitMeasurement(item)
        return unitMeasurement.reference || 'UN'
      },
      getUnitMeasurementName(item) {
        var unitMeasurement = this.getUnitMeasurement(item)
        return unitMeasurement.name || unitMeasurement.reference || 'Unidad'
      },
      toFloat(value) {
        var number = parseFloat(value)
        if(isNaN(number)) {
          return 0
        }
        return number
      },
      getRemitReference(remit) {
        if(!remit) {
          return ''
        }

        var typeVoucher = remit.type_voucher || remit.typeVoucher
        var name = typeVoucher && typeVoucher.name ? typeVoucher.name + ' ' : 'Remito '
        var pointSale = parseInt(remit.point_sale || 0).toString().padStart(4, '0')
        var number = parseInt(remit.number || 0).toString().padStart(8, '0')
        return name + pointSale + '-' + number
      },
      buildLinkedRemits(order) {
        if(!order || !order.details || !order.details.length) {
          return []
        }

        var remits = {}

        order.details.forEach((detail) => {
          var links = detail.purchase_remits_detail || detail.purchaseRemitsDetail || detail.PurchaseRemitsDetail || []
          if(!Array.isArray(links)) {
            return
          }

          links.forEach((link) => {
            var remit = link.remit || link.Remit
            if(!remit || !remit.id) {
              return
            }

            if(!remits[remit.id]) {
              remits[remit.id] = {
                id: remit.id,
                date: remit.date,
                reference: this.getRemitReference(remit),
                supplier: order.supplier ? order.supplier.name : '',
                items: 0,
                quantity: 0,
              }
            }

            remits[remit.id].items = remits[remit.id].items + 1
            remits[remit.id].quantity = remits[remit.id].quantity + parseFloat(link.quantity || 0)
          })
        })

        return Object.values(remits).sort((a, b) => {
          return (b.id || 0) - (a.id || 0)
        })
      },
      mergeOriginMetadata(currentMeta, incomingMeta) {
        var current = currentMeta && typeof currentMeta === 'object' ? currentMeta : {}
        var incoming = incomingMeta && typeof incomingMeta === 'object' ? incomingMeta : {}

        var metrics = []
        if(Array.isArray(current.metrics)) {
          metrics = metrics.concat(current.metrics)
        } else if(current.metric) {
          metrics.push(current.metric)
        }

        if(Array.isArray(incoming.metrics)) {
          metrics = metrics.concat(incoming.metrics)
        } else if(incoming.metric) {
          metrics.push(incoming.metric)
        }

        metrics = metrics.filter((metric, index, arr) => !!metric && arr.indexOf(metric) === index)

        var reasons = []
        if(Array.isArray(current.reasons)) {
          reasons = reasons.concat(current.reasons)
        }
        if(Array.isArray(incoming.reasons)) {
          reasons = reasons.concat(incoming.reasons)
        }
        reasons = reasons.filter((reason, index, arr) => !!reason && arr.indexOf(reason) === index)

        return {
          metric: current.metric || incoming.metric || null,
          metrics: metrics,
          reasons: reasons,
        }
      },
      getDetailOriginMetrics(item) {
        if(item && item.origin_metadata && Array.isArray(item.origin_metadata.metrics) && item.origin_metadata.metrics.length) {
          return item.origin_metadata.metrics.filter((metric) => !!metric)
        }

        if(item && item.origin_metadata && item.origin_metadata.metric) {
          return [item.origin_metadata.metric]
        }

        if(item && item.origin) {
          return [item.origin]
        }

        return ['manual']
      },
      getMetricLabel(metric) {
        var labels = {
          manual: 'Manual',
          supplier_catalog: 'Catálogo',
          last_purchases: 'Últimas compras',
          pending_previous: 'Pendientes',
          low_stock: 'Stock mínimo',
          replica_previous: 'Réplica anterior',
          sold_since_last_remit: 'Vendido post remito',
        }
        return labels[metric] || metric
      },
      getMetricVariant(metric) {
        if(metric === 'supplier_catalog') return 'primary'
        if(metric === 'last_purchases') return 'success'
        if(metric === 'pending_previous') return 'warning'
        if(metric === 'low_stock') return 'danger'
        if(metric === 'replica_previous') return 'info'
        if(metric === 'sold_since_last_remit') return 'dark'
        return 'secondary'
      },
      getSuggestionCodeVariant(item) {
        if(item && item.metrics && item.metrics.length > 1) {
          return 'dark'
        }

        return this.getMetricVariant(item && item.metric ? item.metric : '')
      },
      getSuggestionRowClass(item, type) {
        if(type !== 'row' || !item) {
          return ''
        }

        var classes = []

        if(this.hasPendingWithoutStock(item)) {
          classes.push('suggestion-row-critical')
        } else if(this.isStockBelowMinimum(item)) {
          classes.push('suggestion-row-warning')
        }

        if(parseFloat(item.quantity_draft || 0) > 0) {
          classes.push('table-success')
        }

        if(item.metrics && item.metrics.length > 1) {
          classes.push('table-active')
        }

        return classes.join(' ')
      },
      isStockBelowMinimum(item) {
        if(!item) {
          return false
        }
        return this.toFloat(item.stock) < this.toFloat(item.stock_minimum)
      },
      hasPendingWithoutStock(item) {
        if(!item) {
          return false
        }
        return this.toFloat(item.stock_pending) > 0 && this.toFloat(item.stock) <= 0
      },
      getStockCellTextClass(item) {
        if(this.hasPendingWithoutStock(item)) {
          return 'text-danger font-weight-bold'
        }

        if(this.isStockBelowMinimum(item)) {
          return 'text-warning font-weight-bold'
        }

        return ''
      },
      getSuggestionValueWithUnit(item, value, label) {
        return label + ': ' + this.toFloat(value).toFixed(2) + ' ' + this.getUnitMeasurementReference(item)
      },
      getSuggestionPurchasePrice(item) {
        if(!item || !item.purchase_price) {
          return 0
        }
        return this.suggestions.showPriceWithIva ? parseFloat(item.purchase_price.with_iva || 0) : parseFloat(item.purchase_price.without_iva || 0)
      },
      isSuggestionPurchasePriceFromOtherSupplier(item) {
        return !!(item && item.purchase_price && item.purchase_price.is_other_supplier)
      },
      getSuggestionPurchasePriceSupplierName(item) {
        return (item && item.purchase_price && item.purchase_price.supplier_name) || ''
      },
      getSalesYearlyRows(item) {
        if(!item || !Array.isArray(item.sales_yearly) || !item.sales_yearly.length) {
          return []
        }

        return item.sales_yearly.filter((row) => {
          return row && row.year && this.toFloat(row.quantity) > 0
        })
      },
      getSalesYearlyTooltip(item) {
        var rows = this.getSalesYearlyRows(item)
        if(!rows.length) {
          return 'Sin ventas registradas en los últimos 2 años'
        }

        var unit = this.getUnitMeasurementReference(item)
        var lines = rows.map((row) => {
          return row.year + ': ' + this.toFloat(row.quantity).toFixed(2) + ' ' + unit
        })

        var total = this.toFloat(item.sales_yearly_total)
        if(total > 0) {
          lines.push('Total: ' + total.toFixed(2) + ' ' + unit)
        }

        return lines.join(' | ')
      },
      getSoldSinceLastRemitTooltip(item) {
        if(!item) {
          return ''
        }

        var quantity = this.toFloat(item.quantity_suggested).toFixed(2)
        var unit = this.getUnitMeasurementReference(item)
        var dateFrom = item.date_from ? moment(item.date_from).format('DD/MM/YYYY') : ''
        var dateTo = item.date_to ? moment(item.date_to).format('DD/MM/YYYY') : ''
        var period = ''

        if(dateFrom && dateTo) {
          period = 'Período: ' + dateFrom + ' a ' + dateTo
        } else if(dateFrom) {
          period = 'Desde: ' + dateFrom
        }

        var rows = parseInt(item.times || 0)
        var traces = rows > 0 ? 'Registros: ' + rows : ''

        return ['Vendido: ' + quantity + ' ' + unit, period, traces].filter((value) => value).join(' | ')
      },
      getStatusVariant(status) {
        if(status === 'pending') return 'warning'
        if(status === 'partial') return 'info'
        if(status === 'delivered') return 'success'
        if(status === 'cancelled') return 'danger'
        return 'secondary'
      },
      getStatusLabel(status) {
        if(status === 'pending') return 'Pendiente'
        if(status === 'partial') return 'Entrega parcial'
        if(status === 'delivered') return 'Entregado'
        if(status === 'cancelled') return 'Cancelado'
        return status
      },
      toggleHiddenSuggestion(item) {
        if(!item || !item.id) {
          return
        }

        serviceAPI.toggleHiddenSuggestion(item.id)
        .then((response) => {
          var data = response.data && response.data.data ? response.data.data : response.data
          item.hidden_suggestion = !!data.hidden_suggestion

          var label = item.hidden_suggestion ? 'ocultado' : 'visible'
          this.$awn.success('Item ' + label + ' en sugerencias')
        })
        .catch(error => {
          this.$awn.alert(Error.showError(error))
        })
      },
    }
  }
</script>

<style>
  .table-purchase-orders-detail {
    max-height: 300px;
    overflow: auto;
  }
  .table-purchase-orders-suggestions {
    margin-bottom: 0;
    table-layout: fixed;
    width: max-content;
    min-width: 100%;
  }
  .suggestions-scroll .table-purchase-orders-suggestions thead th {
    position: sticky;
    top: 0;
    z-index: 5;
    background-color: #343a40;
  }
  .table-purchase-orders-suggestions th:nth-child(1),
  .table-purchase-orders-suggestions td:nth-child(1) {
    width: 390px;
    max-width: 390px;
    white-space: normal;
  }
  .table-purchase-orders-suggestions th:nth-child(2),
  .table-purchase-orders-suggestions td:nth-child(2) {
    width: 130px;
    max-width: 130px;
    white-space: normal;
  }
  .table-purchase-orders-suggestions.table-suggestions-with-sold th:nth-child(1),
  .table-purchase-orders-suggestions.table-suggestions-with-sold td:nth-child(1) {
    width: 340px;
    max-width: 340px;
  }
  .table-purchase-orders-suggestions.table-suggestions-with-sold th:nth-child(2),
  .table-purchase-orders-suggestions.table-suggestions-with-sold td:nth-child(2) {
    width: 120px;
    max-width: 120px;
  }
  .suggestions-scroll {
    max-height: 300px;
    overflow: auto;
    border: 1px solid #e5e7eb;
    border-radius: 6px;
  }
  .suggestions-loading-alert {
    position: sticky;
    bottom: 0;
    border-radius: 0;
    border-left: 0;
    border-right: 0;
    border-bottom: 0;
  }
  .detail-table-scroll {
    max-height: 330px;
    overflow-y: auto;
    border: 1px solid #e5e7eb;
    border-radius: 6px;
  }
  .suggestion-product-cell {
    line-height: 1.2;
  }
  .suggestion-code {
    font-size: 11px;
    letter-spacing: 0.4px;
    margin-bottom: 4px;
  }
  .suggestion-name {
    font-weight: 600;
  }
  .suggestion-name-clamp {
    display: -webkit-box;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 2;
    overflow: hidden;
    text-overflow: ellipsis;
    word-break: break-word;
    max-height: 2.4em;
    line-height: 1.2;
  }
  .suggestion-sales-history {
    line-height: 1.15;
    min-width: 110px;
  }
  .suggestion-sales-year {
    font-weight: 700;
    color: #111827;
    letter-spacing: 0.3px;
  }
  .table-purchase-orders-suggestions tbody tr.suggestion-row-warning > td:first-child {
    border-left: 3px solid #ffc107;
  }
  .table-purchase-orders-suggestions tbody tr.suggestion-row-critical > td:first-child {
    border-left: 3px solid #dc3545;
  }
  .table-purchase-orders-suggestions tbody tr.suggestion-row-warning > td {
    background: rgba(255, 193, 7, 0.08);
  }
  .table-purchase-orders-suggestions tbody tr.suggestion-row-critical > td {
    background: rgba(220, 53, 69, 0.08);
  }
  .suggestion-qty-selected {
    border-color: #28a745;
    background-color: #f4fbf6;
  }
  .suggestion-qty-group {
    width: 100%;
    min-width: 118px;
    max-width: 136px;
    margin-left: auto;
  }
  .suggestion-qty-group .form-control {
    text-align: right;
    font-size: 1.05rem;
    font-weight: 600;
  }
  .detail-price-readonly {
    font-weight: 600;
    color: #374151;
  }
  .suggestion-purchase-price {
    text-align: right;
  }
  .suggestion-purchase-price-other {
    font-size: 0.72rem;
    line-height: 1.1;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 130px;
    margin-left: auto;
  }
  .detail-summary-box {
    border: 1px solid #d1d5db;
    border-radius: 6px;
    background: #f9fafb;
    padding: 10px 12px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    font-size: 13px;
  }
  .detail-summary-left {
    color: #6b7280;
  }
  .detail-summary-right {
    color: #111827;
  }
  .table-full-detail-purchase-orders {
    max-height: 350px;
    overflow: auto;
  }
  .purchase-orders-detail-item {
    white-space: normal;
    line-height: 1.2;
  }
  .purchase-orders-table-footer {
    padding-right: 12px;
  }
  .btn.purchase-orders-action-toggle-labeled {
    display: inline-flex !important;
    align-items: center;
    justify-content: flex-start;
    width: auto !important;
    height: auto !important;
    min-height: 0;
    padding: 0 !important;
    border: none !important;
    background: transparent !important;
    box-shadow: none !important;
    color: var(--app-color-text-secondary) !important;
    font-size: 13px;
    line-height: 1.15;
  }
  .btn.purchase-orders-action-toggle-labeled:hover,
  .btn.purchase-orders-action-toggle-labeled:focus {
    color: var(--app-color-primary) !important;
    text-decoration: underline;
  }
  .purchase-orders-form-modal-dialog {
    max-width: min(1400px, 95vw);
  }
  .purchase-orders-form-section {
    border: 1px solid var(--app-color-border);
    border-radius: 8px;
    background: var(--app-color-surface);
    padding: 14px;
  }
  .purchase-orders-form-section__title {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    margin-bottom: 12px;
    color: var(--app-color-text);
    font-size: var(--app-text-sm);
    font-weight: var(--app-font-semibold);
  }
  .purchase-orders-supplier-badge {
    display: inline-flex;
    align-items: center;
    margin-left: 4px;
    padding: 4px 10px;
    font-size: var(--app-text-sm);
    font-weight: var(--app-font-normal);
    color: var(--app-color-text-muted);
    background: var(--app-color-surface-alt, rgba(0, 0, 0, 0.05));
    border: 1px solid var(--app-color-border);
    border-radius: 20px;
  }
  .purchase-orders-detail-action-link {
    display: inline-flex;
    align-items: center;
    justify-content: flex-start;
    min-width: 34px;
    min-height: 28px;
    padding: 3px 7px;
    margin-left: 10px;
    border-radius: 6px;
    color: var(--app-color-text-secondary);
    font-size: 16px;
    line-height: 1.15;
  }
  .purchase-orders-detail-action-link:hover,
  .purchase-orders-detail-action-link:focus {
    background: var(--app-color-primary-soft);
    color: var(--app-color-primary);
    text-decoration: none;
  }
  @media (max-width: 767.98px) {
    .table-full-detail-purchase-orders.app-table.b-table-stacked-md > tbody > tr > td:last-child::before {
      content: attr(data-label) !important;
    }
    .purchase-orders-detail-action-link {
      min-height: 0;
      padding: 0;
      margin-left: 0;
      background: transparent;
      color: var(--app-color-text-secondary);
      font-size: 13px;
    }
    .purchase-orders-detail-action-link:hover,
    .purchase-orders-detail-action-link:focus {
      background: transparent;
      color: var(--app-color-primary);
      text-decoration: underline;
    }
  }
</style>
